import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { groupBy } from '@/lib/utils';

import type { AtsDataObj, JobListContextProps, ListItem } from './types';
import type { AtsJobPosting } from '@/lib/AtsApi/types';
import type { ReactNode } from 'react';

export const JobListContext = createContext<JobListContextProps | undefined>(
  undefined,
);

export const useJobListContext = (): JobListContextProps => {
  const context = useContext(JobListContext);

  if (context === undefined) {
    throw new Error('useJobListContext must be used within a JobListProvider');
  }

  return context;
};

interface JobListProviderProps {
  atsData: AtsDataObj;
  children: ReactNode;
}

export const JobListProvider = ({
  atsData,
  children,
}: JobListProviderProps) => {
  const router = useRouter();

  const [searchTerm, setSearchTerm] = useState('');

  const [filterLocations, setFilterLocations] = useState<string[]>([]);
  const [filterDepartments, setFilterDepartments] = useState<string[]>([]);

  const [filteredJobs, setFilteredJobs] = useState<AtsJobPosting[]>([
    ...atsData.jobPostings,
  ]);

  const sortedJobs = useMemo(
    () =>
      Object.entries<AtsJobPosting[]>(
        groupBy(filteredJobs, 'departmentName'),
      ).sort(),
    [filteredJobs],
  );

  const locationList = useMemo<ListItem[]>(
    () => [
      ...atsData.locations
        .map((location) => ({
          label: location.name,
          value: location.name,
          count: atsData.jobPostings.filter(
            (job) => job.locationName === location.name,
          ).length,
        }))
        .filter((location) => location.count > 0),
    ],
    [atsData.jobPostings, atsData.locations],
  );

  const departmentList = useMemo<ListItem[]>(
    () => [
      ...atsData.departments
        .map((department) => {
          return {
            label: department.name,
            value: department.name,
            count: atsData.jobPostings.filter(
              (job) => job.departmentName === department.name,
            ).length,
          };
        })
        .filter((department) => department.count > 0),
    ],
    [atsData.departments, atsData.jobPostings],
  );

  const hasFiltersSet = useMemo(
    () => !!filterLocations?.length || !!filterDepartments?.length,
    [filterLocations, filterDepartments],
  );

  useEffect(() => {
    const filteredLocationIdsSet = new Set();
    const filteredDepartmentIdsSet = new Set();

    atsData.jobPostings.forEach((job) => {
      if (filterLocations.includes(job.locationName)) {
        filteredLocationIdsSet.add(job.id);
      }

      if (filterDepartments.includes(job.departmentName)) {
        filteredDepartmentIdsSet.add(job.id);
      }
    });

    const filteredLocationIds = [...filteredLocationIdsSet];
    const filteredDepartmentIds = [...filteredDepartmentIdsSet];

    setFilteredJobs(
      atsData.jobPostings
        .filter((job) => {
          if (filteredDepartmentIds.length && filteredLocationIds.length) {
            const allFilterIds = new Set([
              ...filteredLocationIds,
              ...filteredDepartmentIds,
            ]);
            const filtered = Array.from(allFilterIds).filter((value) =>
              filteredDepartmentIds.includes(value) &&
              filteredLocationIds.includes(value)
                ? value
                : false,
            );
            return filtered.includes(job.id);
          }
          if (filteredDepartmentIds.length) {
            return filteredDepartmentIds.includes(job.id);
          }
          if (filteredLocationIds.length) {
            return filteredLocationIds.includes(job.id);
          }

          return true;
        })
        .filter((job) => job.title.toLowerCase().includes(searchTerm)),
    );
  }, [searchTerm, filterLocations, filterDepartments, atsData.jobPostings]);

  useEffect(() => {
    const searchQuery = router?.query?.q;
    if (searchQuery && typeof searchQuery === 'string') {
      setSearchTerm(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const providerValue = useMemo(
    () => ({
      atsData,
      searchTerm,
      setSearchTerm,
      filterLocations,
      filterDepartments,
      setFilterDepartments,
      setFilterLocations,
      departmentList,
      locationList,
      hasFiltersSet,
      sortedJobs,
      filteredJobs,
    }),
    [
      atsData,
      departmentList,
      filterDepartments,
      filterLocations,
      filteredJobs,
      hasFiltersSet,
      locationList,
      searchTerm,
      sortedJobs,
    ],
  );

  return (
    <JobListContext.Provider value={providerValue}>
      {children}
    </JobListContext.Provider>
  );
};
